import React, { useState } from "react";
import { ExportOutlined } from "@ant-design/icons";
import { Button, Drawer, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { type CustomerBundle } from "@fitness-app/data-models/entities/CustomerBundle";
import { type Order } from "@fitness-app/data-models/entities/Order";
import { type ProductClient } from "@fitness-app/data-models/entities/ProductClient";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { getHumanPrice } from "@fitness-app/utils/src/products/getHumanPrice";

import { usePermission } from "~/hooks/usePermission";
import { useProductsList } from "~/modules/Dashboard/AppStats/hooks/useProductsList";
import ProductClientDetails from "~/modules/Products/ProductClientDetails/ProductClientDetails";
import InvoiceDataInfo from "~/modules/Products/components/InvoiceDataInfo/InvoiceDataInfo";
import { PaymentTypeTag } from "~/modules/Products/Product/ProductClientsList/components/PaymentTypeTag";
import { useCustomerBundles } from "./useCustomerBundles";
import { useCustomerProductClients } from "./useCustomerProductClients";
import { type CustomerListItem } from "./useProductsCustomers";

interface Props {
  customer: CustomerListItem;
  /** Dane do faktury — poza zasięgiem konta bez products.manage. Kwoty zostają. */
  showBillingData?: boolean;
}

const bundleTypeColor: Record<string, string> = {
  new_cart: "geekblue",
  woocommerce_main: "purple",
  woocommerce_mb: "magenta",
};

interface BundleItem {
  productName?: string | null;
  priceDescription?: string | null;
  installments?: number | null;
}

const getInstallmentItems = (items: unknown): BundleItem[] => {
  if (!Array.isArray(items)) return [];
  return items.map((i) => (i && typeof i === "object" ? (i as BundleItem) : null)).filter((i): i is BundleItem => !!i);
};

const getBundlePriceLabel = (bundle: CustomerBundle): { label: string | null; installments: number | null } => {
  if (!bundle.metadata || bundle.type !== "new_cart") return { label: null, installments: null };
  const m = bundle.metadata as Record<string, unknown>;
  const items = getInstallmentItems(m.items);
  const installmentItem = items.find((i) => i.installments && i.installments > 1) ?? items[0];
  const label = installmentItem?.priceDescription ?? null;
  const installments =
    typeof m.originalCycles === "number" && m.originalCycles > 1
      ? m.originalCycles
      : installmentItem?.installments && installmentItem.installments > 1
        ? installmentItem.installments
        : null;
  return { label, installments };
};

const getBundleOrderId = (bundle: CustomerBundle): string | null => {
  if (bundle.type === "new_cart" && bundle.metadata && typeof bundle.metadata === "object") {
    const m = bundle.metadata as Record<string, unknown>;
    return m.orderId ? String(m.orderId) : null;
  }
  return bundle.initialOrderId ?? null;
};

const getVariantSlug = (metadata: unknown): string | null => {
  if (!metadata || typeof metadata !== "object") return null;
  const m = metadata as Record<string, unknown>;
  const session = m.sessionMetadata;
  if (!session || typeof session !== "object") return null;
  const slug = (session as Record<string, unknown>).variantSlug;
  return typeof slug === "string" && slug.length > 0 ? slug : null;
};

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

const renderBundleMetadata = (bundle: CustomerBundle, t: TFunc): string => {
  if (!bundle.metadata) return "-";
  if (bundle.type === "new_cart") {
    const m = bundle.metadata as Record<string, unknown>;
    const gatewayRaw = m.paymentGateway ? String(m.paymentGateway) : null;
    const statusRaw = m.paymentStatus ? String(m.paymentStatus) : null;
    const gatewayLabel = gatewayRaw
      ? t(`productTransactions.paymentGateway.${gatewayRaw.toLowerCase()}`, { defaultValue: gatewayRaw })
      : null;
    // Ukryj status "CREATED" — jest błędny (klientki z opłaconymi pakietami nadal mają status CREATED w metadanych)
    const statusLabel =
      statusRaw && statusRaw.toUpperCase() !== "CREATED"
        ? t(`productTransactions.type.${statusRaw.toLowerCase()}`, { defaultValue: statusRaw })
        : null;
    const parts = [
      gatewayLabel ? `${t("customersTab.expander.gateway")}: ${gatewayLabel}` : null,
      statusLabel ? `${t("customersTab.expander.paymentStatus")}: ${statusLabel}` : null,
    ].filter(Boolean);
    return parts.join(" · ") || "-";
  }
  const m = bundle.metadata as Record<string, unknown>;
  const parts = [
    m.originalWooProductId ? `woo: ${String(m.originalWooProductId)}` : null,
    m.totalOrders ? `${t("customersTab.expander.ordersCount")}: ${String(m.totalOrders)}` : null,
    m.totalSpent ? `${t("customersTab.expander.totalSpent")}: ${String(m.totalSpent)}` : null,
  ].filter(Boolean);
  return parts.join(" · ") || "-";
};

export const CustomerExpandedRow: React.FC<Props> = ({ customer, showBillingData = true }) => {
  const { t } = useTranslation(["products", "common", "client", "checkout"]);
  const { list: productsList } = useProductsList();
  const { list: productClients, isLoading: isLoadingClients } = useCustomerProductClients(customer.id);
  const { list: sortedBundles, isLoading: isLoadingBundles } = useCustomerBundles(
    [customer.email, ...(customer.emailAliases ?? [])].filter((email): email is string => Boolean(email)),
  );

  const productNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    productsList.forEach((p) => map.set(p.value, p.label));
    return map;
  }, [productsList]);

  const customerVariantSlug = React.useMemo(() => getVariantSlug(customer.metadata), [customer.metadata]);

  const navigate = useNavigate();
  // Trasa `/products/:id/clients/:id` siedzi za `products.manage ALL` - bez tego
  // uprawnienia osobny ekran przekierowałby na zakładkę materiałów (jak w ClientPackageDrawer).
  const canManageProducts = usePermission(PermissionKey.PRODUCTS_MANAGE, PermissionScope.ALL);
  const [selectedClient, setSelectedClient] = useState<ProductClient | null>(null);
  const getProductName = (row: ProductClient) =>
    row.product?.name ?? productNameById.get(row.productId) ?? row.productId;
  const selectedClientUrl = selectedClient
    ? `/products/${selectedClient.productId}/clients/${selectedClient.id}`
    : null;

  return (
    <Space direction="vertical" size={24} style={{ width: "100%" }}>
      <div>
        <Typography.Title level={5} style={{ marginBottom: 12 }}>
          {t("customersTab.expander.bundles")} ({sortedBundles.length})
        </Typography.Title>

        {!isLoadingBundles && sortedBundles.length === 0 ? (
          <Typography.Text type="secondary">{t("common:noData")}</Typography.Text>
        ) : (
          <Table<CustomerBundle>
            rowKey="id"
            size="small"
            pagination={false}
            loading={isLoadingBundles}
            dataSource={sortedBundles}
          >
            <Table.Column<CustomerBundle> title="#" width={40} render={(_, __, i) => i + 1} />
            <Table.Column<CustomerBundle>
              title={t("customersTab.expander.type")}
              dataIndex="type"
              render={(type: string) => (
                <Tag color={bundleTypeColor[type] ?? "default"}>
                  {t(`customersTab.bundleType.${type}`, { defaultValue: type })}
                </Tag>
              )}
            />
            <Table.Column<CustomerBundle>
              title={t("customersTab.expander.product")}
              dataIndex="productId"
              render={(productId: string | null, row) =>
                productId ? (
                  <Link to={`/products/${productId}/`}>{productNameById.get(productId) ?? row.name ?? productId}</Link>
                ) : (
                  (row.name ?? "-")
                )
              }
            />
            <Table.Column<CustomerBundle>
              title={t("checkout:orderNo")}
              render={(_, row) => {
                const orderId = getBundleOrderId(row);
                return orderId ? (
                  <Typography.Text copyable={{ text: orderId }} style={{ fontSize: 12 }}>
                    {orderId.slice(0, 8)}…
                  </Typography.Text>
                ) : (
                  "-"
                );
              }}
            />
            <Table.Column<CustomerBundle>
              title={t("customersTab.expander.amount")}
              dataIndex="amount"
              render={(amount: number, row) => (amount != null ? <b>{getHumanPrice(amount, row.currency)}</b> : "-")}
            />
            <Table.Column<CustomerBundle>
              title={t("customersTab.expander.priceLabel")}
              render={(_, row) => {
                const { label } = getBundlePriceLabel(row);
                return label ? <Typography.Text style={{ fontSize: 12 }}>{label}</Typography.Text> : "-";
              }}
            />
            <Table.Column<CustomerBundle>
              title={t("customersTab.expander.installments")}
              render={(_, row) => {
                const { installments } = getBundlePriceLabel(row);
                if (!installments) return "-";
                return <Tag color="gold">{t("customersTab.expander.installmentsCount", { count: installments })}</Tag>;
              }}
            />
            <Table.Column<CustomerBundle>
              title={t("customersTab.expander.variant")}
              render={(_, row) => {
                const slug = getVariantSlug(row.metadata) ?? customerVariantSlug;
                return slug ? <Typography.Text style={{ fontSize: 12 }}>{slug}</Typography.Text> : "-";
              }}
            />
            <Table.Column<CustomerBundle>
              title={t("customersTab.expander.createdAt")}
              dataIndex="createdAt"
              render={(createdAt: string) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
            />
            <Table.Column<CustomerBundle>
              title={t("customersTab.expander.metadata")}
              render={(_, row) => (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {renderBundleMetadata(row, t as TFunc)}
                </Typography.Text>
              )}
            />
          </Table>
        )}
      </div>

      <div>
        <Typography.Title level={5} style={{ marginBottom: 12 }}>
          {t("customersTab.expander.productClients")} ({productClients.length})
        </Typography.Title>

        <Table<ProductClient>
          rowKey="id"
          size="small"
          pagination={false}
          loading={isLoadingClients}
          dataSource={productClients}
          locale={{ emptyText: t("common:noData") }}
        >
          <Table.Column<ProductClient>
            title={t("customersTab.expander.product")}
            dataIndex="productId"
            render={(productId: string, row) => (
              <Typography.Link onClick={() => setSelectedClient(row)}>{getProductName(row)}</Typography.Link>
            )}
          />
          <Table.Column<ProductClient>
            title={t("customersTab.expander.status")}
            dataIndex="status"
            render={(status: ProductClient["status"]) => (
              <Tag color={status === "active" ? "green" : "default"}>
                {t(`clientStatus.${status}`, { defaultValue: status })}
              </Tag>
            )}
          />
          <Table.Column<ProductClient>
            title={t("customersTab.expander.paymentType")}
            dataIndex="paymentType"
            render={(_, row) => <PaymentTypeTag productClient={row} />}
          />
          <Table.Column<ProductClient>
            title={t("customersTab.expander.accessPeriod")}
            render={(_, row) => {
              const ap = "accessPeriod" in row ? row.accessPeriod : null;
              if (!ap) return "-";
              const start = ap.currentPeriodStart ? dayjs.unix(ap.currentPeriodStart).format("DD.MM.YYYY") : null;
              const end =
                "currentPeriodEnd" in ap && ap.currentPeriodEnd
                  ? dayjs.unix(ap.currentPeriodEnd).format("DD.MM.YYYY")
                  : null;
              return [start, end].filter(Boolean).join(" → ") || "-";
            }}
          />
          <Table.Column<ProductClient>
            title={t("customersTab.expander.createdAt")}
            dataIndex="createdAt"
            render={(createdAt: string) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
          />
          <Table.Column<ProductClient>
            title={t("customersTab.expander.actions")}
            render={(_, row) => (
              <Typography.Link className="whitespace-nowrap" onClick={() => setSelectedClient(row)}>
                {t("common:details")}
              </Typography.Link>
            )}
          />
        </Table>
      </div>

      {selectedClient && selectedClientUrl ? (
        <Drawer
          open
          width="min(1100px, 92vw)"
          onClose={() => setSelectedClient(null)}
          destroyOnClose
          styles={{ body: { paddingTop: 8 } }}
          title={getProductName(selectedClient)}
          extra={
            canManageProducts ? (
              <Space size={0}>
                <Button type="link" onClick={() => navigate(selectedClientUrl)}>
                  Otwórz jako osobny ekran
                </Button>
                <Button
                  style={{ marginLeft: -12 }}
                  icon={<ExportOutlined />}
                  title="Otwórz w nowym oknie"
                  href={`${window.location.origin}${selectedClientUrl}`}
                  target="_blank"
                  type="link"
                />
              </Space>
            ) : null
          }
        >
          <ProductClientDetails
            embedded
            readonly={false}
            productId={selectedClient.productId}
            clientEmail={selectedClient.id}
          />
        </Drawer>
      ) : null}

      {showBillingData && customer.client ? (
        <div>
          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            {t("customersTab.expander.invoiceData")}
          </Typography.Title>
          <InvoiceDataInfo
            orderClient={customer.client as Order["client"]}
            withTitle={false}
            phoneNumber={customer.phoneNumber}
          />
        </div>
      ) : null}
    </Space>
  );
};
